(() => {
  'use strict';

  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));
  const normalizeAngle = value => ((value + 180) % 360 + 360) % 360 - 180;
  const radians = degrees => degrees * Math.PI / 180;
  const degrees = value => value * 180 / Math.PI;
  const STORAGE_KEY = 'kgm-maison:v30.8:five-room-placement';
  const SESSION_KEY = 'kgm-placement-session';
  const initialized = new WeakSet();
  let closeActiveEditor = null;

  const placementAllowed = () => {
    const params = new URLSearchParams(location.search);
    let sessionAllowed = false;
    try { sessionAllowed = sessionStorage.getItem(SESSION_KEY) === '1'; } catch (_) {}
    return Boolean(window.Shopify?.designMode) || params.get('kgmPlacement') === '1' || sessionAllowed;
  };

  const roomFor = element => element?.closest?.('[data-kgmj-scene]')?.dataset.kgmjScene || '';
  const allPlacementNodes = section => [...section.querySelectorAll('[data-placement-id][data-placement-role]')];
  const activeScene = section => section.matches?.('[data-kgmj-scene]')
    ? section
    : section.querySelector('[data-kgmj-scene].is-active') || section.querySelector('[data-kgmj-scene]');
  const activePano = section => activeScene(section)?.querySelector('[data-kgmj-sphere="true"],[data-kgm-room-360]') || null;
  const activeNodes = section => [...(activePano(section)?.querySelectorAll('[data-placement-id][data-placement-role]') || [])];
  const nodeSlot = node => String(node?.dataset.placementSlot || node?.dataset.placementId?.split('-').pop() || '1');

  const snapshot = node => ({
    yaw: Number(node.dataset.yaw || 0),
    pitch: Number(node.dataset.pitch || 0),
    ...(node.dataset.placementRole === 'product' ? {
      scale: Number(node.dataset.scale || 1),
      depth: Number(node.dataset.depth || 1),
      rotation: Number(node.dataset.rotation || 0),
      tilt: Number(node.dataset.tilt || 0)
    } : {})
  });

  const applyValues = (node, values) => {
    if (!node || !values) return;
    const keys = node.dataset.placementRole === 'product'
      ? ['yaw', 'pitch', 'scale', 'depth', 'rotation', 'tilt']
      : ['yaw', 'pitch'];
    keys.forEach(key => {
      if (Number.isFinite(Number(values[key]))) node.dataset[key] = String(Number(values[key]));
    });
  };

  const initPlacement = section => {
    if (!section || initialized.has(section) || !placementAllowed()) return;
    const panel = section.querySelector('[data-kgmj-placement-panel]');
    const launch = section.querySelector('[data-kgmj-placement-launch]');
    if (!panel || !launch) return;
    initialized.add(section);
    section.classList.add('kgmj-dev-tools-enabled');
    launch.hidden = false;
    panel.hidden = false;

    const defaults = new Map();
    allPlacementNodes(section).forEach(node => {
      defaults.set(`${roomFor(node)}:${node.dataset.placementId}:${node.dataset.placementRole}`, snapshot(node));
    });

    let slot = '1';
    let role = 'product';
    let editorOpen = false;
    let drag = null;
    let savedConfig = null;
    const status = panel.querySelector('[data-kgmj-placement-status]');
    const productControls = panel.querySelector('[data-kgmj-placement-product-controls]');
    const controlInputs = [...panel.querySelectorAll('[data-placement-control]')];

    const currentRoom = () => activeScene(section)?.dataset.kgmjScene || '';
    const currentId = () => `${currentRoom()}-${slot}`;
    const currentTarget = () => activeNodes(section).find(node =>
      node.dataset.placementRole === role &&
      (nodeSlot(node) === slot || node.dataset.placementId === currentId())
    ) || null;

    const setStatus = message => {
      if (status) status.textContent = message;
      clearTimeout(setStatus.timer);
      setStatus.timer = setTimeout(() => {
        if (status) status.textContent = 'Development-only placement. Changes stay in this browser until copied into the theme.';
      }, 3600);
    };

    const renderPlacement = () => {
      const pano = activePano(section);
      if (!pano) return;
      const state = pano._kgmLast360State || { yaw: 0, pitch: 0, zoom: 1 };
      window.KGMRoom360?.projectHotspots?.(pano, state, activeNodes(section));
    };

    const restoreSavedForRoom = () => {
      const room = currentRoom();
      const roomConfig = savedConfig?.rooms?.[room];
      if (!roomConfig) return;
      activeNodes(section).forEach(node => {
        const bucket = node.dataset.placementRole === 'product' ? roomConfig.products : roomConfig.hotspots;
        applyValues(node, bucket?.[node.dataset.placementId]);
      });
      renderPlacement();
    };

    const syncPanel = () => {
      const room = currentRoom();
      const roomOutput = panel.querySelector('[data-kgmj-placement-room]');
      if (roomOutput) roomOutput.textContent = activeScene(section)?.dataset.title || room;

      panel.querySelectorAll('[data-kgmj-placement-select]').forEach(button => {
        const buttonSlot = String(button.dataset.placementSlot || button.dataset.kgmjPlacementSelect?.split('-').pop() || '1');
        button.dataset.placementSlot = buttonSlot;
        button.dataset.kgmjPlacementSelect = `${room}-${buttonSlot}`;
        button.classList.toggle('is-active', buttonSlot === slot);
        button.setAttribute('aria-pressed', String(buttonSlot === slot));
      });
      panel.querySelectorAll('[data-kgmj-placement-target]').forEach(button => {
        const selected = button.dataset.kgmjPlacementTarget === role;
        button.classList.toggle('is-active', selected);
        button.setAttribute('aria-pressed', String(selected));
      });

      const target = currentTarget();
      productControls?.toggleAttribute('hidden', role !== 'product');
      allPlacementNodes(section).forEach(node => node.classList.toggle('is-placement-selected', node === target && editorOpen));
      if (!target) return;

      const yawOutput = panel.querySelector('[data-placement-readout="yaw"]');
      const pitchOutput = panel.querySelector('[data-placement-readout="pitch"]');
      if (yawOutput) yawOutput.textContent = `${Number(target.dataset.yaw || 0).toFixed(1)}°`;
      if (pitchOutput) pitchOutput.textContent = `${Number(target.dataset.pitch || 0).toFixed(1)}°`;

      if (role === 'product') {
        controlInputs.forEach(input => {
          const key = input.dataset.placementControl;
          const value = Number(target.dataset[key] || (key === 'scale' || key === 'depth' ? 1 : 0));
          input.value = String(value);
          const output = panel.querySelector(`[data-placement-output="${key}"]`);
          if (output) output.textContent = key === 'rotation' || key === 'tilt' ? `${Math.round(value)}°` : value.toFixed(2);
        });
      }
    };

    const openEditor = () => {
      if (closeActiveEditor && closeActiveEditor !== closeEditor) closeActiveEditor();
      closeActiveEditor = closeEditor;
      editorOpen = true;
      restoreSavedForRoom();
      section.classList.add('kgmj-placement-active');
      panel.setAttribute('aria-hidden', 'false');
      launch.setAttribute('aria-expanded', 'true');
      syncPanel();
      setStatus('Select a product or hotspot, then drag it directly in the active room.');
    };

    const closeEditor = () => {
      editorOpen = false;
      drag = null;
      section.classList.remove('kgmj-placement-active', 'kgmj-placement-dragging');
      panel.setAttribute('aria-hidden', 'true');
      launch.setAttribute('aria-expanded', 'false');
      allPlacementNodes(section).forEach(node => node.classList.remove('is-placement-selected'));
      if (closeActiveEditor === closeEditor) closeActiveEditor = null;
      launch.focus?.({ preventScroll: true });
    };

    const fullConfig = () => {
      let persisted = savedConfig;
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) persisted = JSON.parse(raw);
      } catch (_) {}
      const result = { version: '30.8-room-parity', rooms: { ...(persisted?.rooms || {}) } };
      allPlacementNodes(section).forEach(node => {
        const room = roomFor(node);
        result.rooms[room] ||= { products: {}, hotspots: {} };
        const bucket = node.dataset.placementRole === 'product' ? result.rooms[room].products : result.rooms[room].hotspots;
        bucket[node.dataset.placementId] = snapshot(node);
      });
      return result;
    };

    const screenToSphere = (pano, clientX, clientY) => {
      const rect = pano.getBoundingClientRect();
      const state = pano._kgmLast360State || { yaw: 0, pitch: 0, zoom: 1 };
      const x = ((clientX - rect.left) / Math.max(rect.width, 1) - 0.5) * 2;
      const screenY = ((clientY - rect.top) / Math.max(rect.height, 1) - 0.5) * 2;
      const aspect = Math.max(0.35, rect.width / Math.max(rect.height, 1));
      const tangent = Math.tan(radians(clamp(68 / (state.zoom || 1), 31, 100)) / 2);
      let cameraX = x * aspect * tangent;
      let cameraY = -screenY * tangent;
      let cameraZ = 1;
      const length = Math.hypot(cameraX, cameraY, cameraZ) || 1;
      cameraX /= length;
      cameraY /= length;
      cameraZ /= length;

      const pitch = radians(state.pitch || 0);
      const cp = Math.cos(pitch);
      const sp = Math.sin(pitch);
      const pitchedY = cp * cameraY + sp * cameraZ;
      const pitchedZ = -sp * cameraY + cp * cameraZ;
      const yaw = radians(state.yaw || 0);
      const cy = Math.cos(yaw);
      const sy = Math.sin(yaw);
      const worldX = cy * cameraX + sy * pitchedZ;
      const worldZ = -sy * cameraX + cy * pitchedZ;
      return {
        yaw: normalizeAngle(degrees(Math.atan2(worldX, worldZ))),
        pitch: clamp(degrees(Math.asin(clamp(pitchedY, -1, 1))), -72, 72)
      };
    };

    launch.addEventListener('click', event => {
      event.preventDefault();
      editorOpen ? closeEditor() : openEditor();
    });
    panel.querySelector('[data-kgmj-placement-close]')?.addEventListener('click', closeEditor);

    panel.querySelectorAll('[data-kgmj-placement-select]').forEach(button => button.addEventListener('click', () => {
      slot = String(button.dataset.placementSlot || button.dataset.kgmjPlacementSelect?.split('-').pop() || '1');
      syncPanel();
    }));
    panel.querySelectorAll('[data-kgmj-placement-target]').forEach(button => button.addEventListener('click', () => {
      role = button.dataset.kgmjPlacementTarget || 'product';
      syncPanel();
    }));

    controlInputs.forEach(input => input.addEventListener('input', () => {
      const target = currentTarget();
      if (!target || target.dataset.placementRole !== 'product') return;
      target.dataset[input.dataset.placementControl] = String(Number(input.value));
      renderPlacement();
      syncPanel();
    }));

    panel.querySelector('[data-kgmj-placement-reset]')?.addEventListener('click', () => {
      const target = currentTarget();
      if (!target) return;
      const values = defaults.get(`${currentRoom()}:${target.dataset.placementId}:${target.dataset.placementRole}`);
      applyValues(target, values);
      renderPlacement();
      syncPanel();
      setStatus('Selected placement reset to its theme default.');
    });

    panel.querySelector('[data-kgmj-placement-save]')?.addEventListener('click', () => {
      savedConfig = fullConfig();
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(savedConfig));
        setStatus('This room was saved locally in this development browser.');
      } catch (_) {
        setStatus('This browser did not allow local placement storage.');
      }
    });

    panel.querySelector('[data-kgmj-placement-copy]')?.addEventListener('click', async () => {
      const text = JSON.stringify(fullConfig(), null, 2);
      try {
        await navigator.clipboard.writeText(text);
      } catch (_) {
        const textarea = document.createElement('textarea');
        textarea.value = text;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        textarea.remove();
      }
      setStatus('Placement JSON copied.');
    });

    section.addEventListener('pointerdown', event => {
      if (!editorOpen) return;
      const target = event.target.closest?.('[data-placement-id][data-placement-role]');
      if (!target || !activePano(section)?.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      slot = nodeSlot(target);
      role = target.dataset.placementRole;
      drag = { target, id: event.pointerId };
      target.setPointerCapture?.(event.pointerId);
      section.classList.add('kgmj-placement-dragging');
      const point = screenToSphere(activePano(section), event.clientX, event.clientY);
      target.dataset.yaw = point.yaw.toFixed(2);
      target.dataset.pitch = point.pitch.toFixed(2);
      renderPlacement();
      syncPanel();
    }, true);

    section.addEventListener('pointermove', event => {
      if (!drag || drag.id !== event.pointerId) return;
      event.preventDefault();
      event.stopPropagation();
      const point = screenToSphere(activePano(section), event.clientX, event.clientY);
      drag.target.dataset.yaw = point.yaw.toFixed(2);
      drag.target.dataset.pitch = point.pitch.toFixed(2);
      renderPlacement();
      syncPanel();
    }, true);

    const endDrag = event => {
      if (!drag || (event.pointerId != null && event.pointerId !== drag.id)) return;
      drag.target.releasePointerCapture?.(drag.id);
      drag = null;
      section.classList.remove('kgmj-placement-dragging');
      setStatus('Position updated. Save locally or copy JSON when ready.');
    };
    section.addEventListener('pointerup', endDrag, true);
    section.addEventListener('pointercancel', endDrag, true);

    section.addEventListener('wheel', event => {
      if (!editorOpen) return;
      const target = event.target.closest?.('[data-placement-role="product"]');
      if (!target || !activePano(section)?.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      slot = nodeSlot(target);
      role = 'product';
      target.dataset.scale = String(clamp(Number(target.dataset.scale || 1) - event.deltaY * 0.0015, 0.25, 2.75).toFixed(2));
      renderPlacement();
      syncPanel();
    }, { passive: false, capture: true });

    section.addEventListener('click', event => {
      if (event.target.closest('[data-kgmj-room]')) {
        requestAnimationFrame(() => {
          restoreSavedForRoom();
          syncPanel();
        });
      }
      if (!editorOpen) return;
      const target = event.target.closest?.('[data-placement-id][data-placement-role]');
      if (!target) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    }, true);

    section.addEventListener('kgm:room360-ready', () => {
      restoreSavedForRoom();
      syncPanel();
    });

    document.addEventListener('click', event => {
      if (!editorOpen) return;
      if (event.target.closest?.('[data-kgmj-room],[data-kgmj-prev],[data-kgmj-next],[data-kgmj-show-foyer],[data-kgmj-back-maison]')) closeEditor();
    });
    document.addEventListener('kgm:open-room', () => {
      if (editorOpen) closeEditor();
    });

    document.addEventListener('keydown', event => {
      if (!editorOpen) return;
      if (event.key === 'Escape') {
        closeEditor();
        return;
      }
      if (event.target.matches?.('input,select,textarea,[contenteditable="true"]')) return;
      const target = currentTarget();
      if (!target) return;
      const step = event.shiftKey ? 2 : 0.35;
      let changed = true;
      if (event.key === 'ArrowLeft') target.dataset.yaw = String(normalizeAngle(Number(target.dataset.yaw || 0) - step));
      else if (event.key === 'ArrowRight') target.dataset.yaw = String(normalizeAngle(Number(target.dataset.yaw || 0) + step));
      else if (event.key === 'ArrowUp') target.dataset.pitch = String(clamp(Number(target.dataset.pitch || 0) + step, -72, 72));
      else if (event.key === 'ArrowDown') target.dataset.pitch = String(clamp(Number(target.dataset.pitch || 0) - step, -72, 72));
      else changed = false;
      if (!changed) return;
      event.preventDefault();
      renderPlacement();
      syncPanel();
    });

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) savedConfig = JSON.parse(raw);
    } catch (_) {}
    restoreSavedForRoom();
    syncPanel();
  };

  const boot = root => {
    const scopes = new Set();
    if (root.matches?.('[data-kgmj-sphere="true"]')) {
      const scene = root.closest('[data-kgmj-scene]');
      if (scene) scopes.add(scene);
    }
    root.querySelectorAll?.('[data-kgmj-sphere="true"]').forEach(pano => {
      const scene = pano.closest('[data-kgmj-scene]');
      if (scene) scopes.add(scene);
    });
    scopes.forEach(initPlacement);
  };

  const enableWithShortcut = event => {
    if (!event.shiftKey || event.key.toLowerCase() !== 'p' || placementAllowed()) return;
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch (_) {}
    boot(document);
  };
  document.addEventListener('keydown', enableWithShortcut);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot(document), { once: true });
  else boot(document);
  document.addEventListener('shopify:section:load', event => boot(event.target || document));
})();
